<template>
  <div class="flex flex-col h-full">
    <ThreadsNav :show-back="true" :show-center="false">
      <template #right>
        <button
          class="btn btn-sm"
          @click="showFriendMateSelect = true"
          v-if="activeTab === 'others'"
        >
          {{ $t("interactive.importOCs") }}
        </button>
      </template>
    </ThreadsNav>

    <div
      v-if="loading"
      class="flex-1 flex flex-col items-center justify-center"
    >
      <div class="loading loading-ring loading-lg"></div>
    </div>

    <div v-else class="flex-1 flex flex-col overflow-hidden">
      <!-- Tabs -->
      <div class="px-4 overflow-auto">
        <VanTabs v-model:active="activeTab" swipeable>
          <VanTab :title="$t('interactive.browseOtherOCs')" name="others">
            <div v-if="interactiveStore.relationshipList.length">
              <HomeInteractiveRelationItem
                v-for="(
                  relationship, index
                ) in interactiveStore.relationshipList"
                :key="index"
                :relationship="relationship"
                :is-mine="false"
              />
            </div>
            <div v-else class="pt-10 flex flex-col items-center">
              <div class="text-center text-base-content/60 px-8">
                <p>{{ $t("interactive.noRelationshipsYet") }}</p>
              </div>
              <div class="mt-8 flex flex-col gap-2">
                <button class="btn btn-sm" @click="dispatchOCs">
                  {{ $t("interactive.dispatchOCs") }}
                </button>

                <button class="btn btn-sm" @click="showFriendMateSelect = true">
                  {{ $t("interactive.importOCs") }}
                </button>
              </div>
            </div>
          </VanTab>

          <VanTab :title="$t('interactive.myOCs')" name="mine">
            <div class="flex justify-between py-2 pr-2">
              <button
                class="btn btn-outline btn-xs w-16 max-w-24 line-clamp-1"
                @click="showFilterOc = true"
              >
                {{ filterOc?.name || $t("interactive.allOCs") }}
              </button>
              <button class="btn btn-primary btn-xs" @click="addRelationship">
                {{ $t("interactive.addRelationship") }}
              </button>
            </div>

            <div v-if="interactiveStore.myRelationshipList.length">
              <div class="flex flex-col gap-3">
                <HomeInteractiveRelationItem
                  v-for="(relationship, index) in filterOcList"
                  :key="index"
                  :relationship="relationship"
                  :is-mine="true"
                />
              </div>
            </div>
          </VanTab>
        </VanTabs>
      </div>
    </div>
    <ModalFriendMateSelect
      v-model:show="showFriendMateSelect"
      @select="onImportOCs"
    />
    <ModalSelectOption
      v-model:show="showFilterOc"
      :options="filterOcOptions"
      @confirm="onFilterOcConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onActivated } from "vue";
import { useRouter } from "vue-router";
import { useInteractive } from "@/stores/interactive";
import { Mate } from "@/types/entity";
import { useI18n } from "vue-i18n";
import { showConfirmDialog, showLoadingToast, showSuccessToast } from "vant";
import { useMate } from "@/stores/mate";
import { useAddRelationPopup } from "@/composable/relation/addRelationPopup";

const { addRelationship } = useAddRelationPopup();

const router = useRouter();
const { t } = useI18n();
const interactiveStore = useInteractive();
const activeTab = ref("others");
const showFriendMateSelect = ref(false);
const filterOc = ref<Mate>();

const filterOcList = computed(() => {
  if (!filterOc.value) return interactiveStore.myRelationshipList;
  return interactiveStore.myRelationshipList.filter(
    (v) =>
      v.sourceMate?.id === filterOc.value?.id ||
      v.targetMate?.id === filterOc.value?.id
  );
});

// Dispatch OCs function
const dispatchOCs = () => {
  router.push({ name: "missionHome" });
};

// const uiStore = useUI();
const onImportOCs = async (mate: Mate) => {
  console.log(mate);
  try {
    await showConfirmDialog({
      message: t("interactive.importOCConfirm", {
        name: mate.name,
      }),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    return;
  }
  const loading = showLoadingToast(t(`Message.importing`));
  await interactiveStore.importOC(mate.id);
  loading.close();
  showSuccessToast(t(`Message.importingSuccess`));
  showFriendMateSelect.value = false;
};

// Edit relationship function
// const editRelationship = (relationship: MateRelationship) => {
//   router.push({
//     name: "relationshipMy",
//     query: {
//       id: relationship.id,
//     },
//   });
// };

const showFilterOc = ref(false);
const mateStore = useMate();
const filterOcOptions = computed(() => {
  return [
    { text: t(`interactive.allOCs`), value: null },
    ...mateStore.list.map((v) => ({
      text: v.name,
      value: v.id,
    })),
  ];
});
const onFilterOcConfirm = (mateId?: number) => {
  filterOc.value = mateStore.list.find((v) => v.id === mateId);
  showFilterOc.value = false;
};

const loading = ref(false);
// Fetch relationships when component is mounted
onActivated(() => {
  Promise.all([
    interactiveStore.getRelationshipList({ targetType: "others" }),
    interactiveStore.getRelationshipList({ targetType: "mine" }),
  ]);
});

onMounted(async () => {
  loading.value = true;
  await Promise.all([
    interactiveStore.getRelationshipList({ targetType: "others" }),
    interactiveStore.getRelationshipList({ targetType: "mine" }),
  ]);
  loading.value = false;
});
</script>
